import { useCallback, useEffect, useState } from 'react';
import { Cpu, Loader2, Plus } from 'lucide-react';
import { api } from '../api';
import { useAvisos } from '../lib/contextoAvisos';
import Ayuda from '../ui/Ayuda';
import NotaExplicativa from '../ui/NotaExplicativa';

/* =====================================================================
   Firmware · TC-23
   =====================================================================
   Publicar una versión no la ofrece: son dos pasos a propósito. Retirar
   una la deja de ofrecer sin tocar a quien ya la tiene.
   ===================================================================== */
export default function Firmware() {
  const avisos = useAvisos();
  const [tick, setTick] = useState(0);
  const recargar = useCallback(() => setTick((t) => t + 1), []);
  const [lista, setLista] = useState(null);
  const [error, setError] = useState('');
  const [f, setF] = useState({ version: '', notas: '' });

  useEffect(() => {
    let vivo = true;
    api.get('/dispositivos/firmware').then((l) => { if (vivo) { setLista(l); setError(''); } }).catch((e) => { if (vivo) setError(e.message); });
    return () => { vivo = false; };
  }, [tick]);

  async function accion(fn, exito) {
    try { await fn(); avisos.exito(exito); recargar(); } catch (e) { avisos.error(e.message); }
  }

  if (error && !lista) return <p className="p-4 text-sm text-red-700">{error}</p>;
  if (!lista) {
    return (
      <p className="flex items-center gap-2 p-4 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Leyendo las versiones…
      </p>
    );
  }
  const listo = f.version.trim().length >= 3;
  return (
    <div className="flex max-w-4xl flex-col gap-5 p-4">
      <header>
        <h1 className="text-xl font-semibold text-gray-900">Actualizar las terminales</h1>
        <p className="text-gray-600">Qué versión se ofrece a las terminales, cuál tiene cada una, y retirar la que salió mal.</p>
        <Ayuda id="terminal.firmware.pantalla" />
      </header>
      {lista.length === 0 ? (
        <NotaExplicativa titulo="Ninguna versión publicada">Publica la primera con su número (el que compila `compilar-firmware.sh`) y después ofrécela: las terminales la bajan en su siguiente latido.</NotaExplicativa>
      ) : (
        <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200 text-sm">{lista.map((v) => (
          <li key={v.id} className="flex flex-wrap items-center gap-x-3 p-3">
            <Cpu className="h-4 w-4 text-gray-400" aria-hidden="true" />
            <span className="font-mono font-medium">{v.version}</span>
            <span className={`rounded px-2 text-xs ${v.estado === 'OFRECIDA' ? 'bg-green-50 text-green-800' : v.estado === 'RETIRADA' ? 'bg-red-50 text-red-800' : 'bg-gray-100 text-gray-700'}`}>{v.estado}</span>
            <span className="text-gray-500">publicada {String(v.publicada_en).slice(0, 10)}</span>
            <span className="text-gray-600">{v.terminales} terminal(es) la tienen</span>
            {v.notas && <span className="text-gray-500 italic">{v.notas}</span>}
            <span className="ml-auto flex gap-2">
              {v.estado !== 'OFRECIDA' && v.estado !== 'RETIRADA' && <button type="button" onClick={() => accion(() => api.post(`/dispositivos/firmware/${encodeURIComponent(v.version)}/ofrecer`), `Se ofrece ${v.version}: las terminales la bajan en su siguiente latido.`)} className="rounded-xl bg-[var(--color-marca)] px-3 py-1 text-xs font-semibold text-white">Ofrecer</button>}
              {v.estado === 'OFRECIDA' && <button type="button" onClick={() => { if (window.confirm(`Retirar ${v.version} deja de ofrecerla; quien ya la tiene se queda con ella. ¿Seguro?`)) accion(() => api.post(`/dispositivos/firmware/${encodeURIComponent(v.version)}/retirar`), `${v.version} retirada.`); }} className="rounded-xl border border-red-300 px-3 py-1 text-xs font-semibold text-red-700">Retirar</button>}
            </span>
          </li>))}</ul>
      )}
      <form onSubmit={(e) => { e.preventDefault(); accion(() => api.post('/dispositivos/firmware', { version: f.version.trim(), notas: f.notas || null }), `Versión ${f.version.trim()} publicada. Todavía no se ofrece.`).then(() => setF({ version: '', notas: '' })); }}
            className="flex flex-wrap items-end gap-2 rounded-xl border border-gray-200 p-4">
        <label className="flex flex-col text-sm">Versión *<input value={f.version} onChange={(e) => setF({ ...f, version: e.target.value })} placeholder="1.4.0" className="rounded-lg border border-gray-300 p-2" /></label>
        <label className="flex flex-1 flex-col text-sm">Notas<input value={f.notas} onChange={(e) => setF({ ...f, notas: e.target.value })} placeholder="Qué cambia" className="rounded-lg border border-gray-300 p-2" /></label>
        <button type="submit" disabled={!listo} className="inline-flex items-center gap-1 rounded-xl bg-[var(--color-marca)] px-3 py-2 text-sm font-semibold text-white disabled:bg-gray-300"><Plus className="h-4 w-4" aria-hidden="true" /> Publicar</button>
        {/* EX-1 (parche del integrador en el merge, decisión del dueño): el botón apagado dice por qué. */}
        {!listo && <span className="text-xs text-amber-700">Falta la versión (por ejemplo 1.4.0).</span>}
      </form>
    </div>
  );
}
